import momentTimezone from 'moment-timezone'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'

const KW_PER_MW = 1000

/**
 * Convert a kW value to MW, rounded to a whole number.
 * Null/undefined values are passed through unchanged (data may be incomplete).
 * @param {number|null|undefined} value - capacity in kW
 * @returns {number|null|undefined} capacity in MW (rounded) or the original empty value
 */
export const kwToMw = value =>
  value == null ? value : Math.round(value / KW_PER_MW)

/**
 * Format the backend's UTC timestamp ("2026-07-08T10:59:25", no zone suffix)
 * as "YYYY-MM-DD HH:MM" converted to the user's timezone (from local storage,
 * falling back to UTC).
 * @param {string|null|undefined} value - UTC timestamp from the backend
 * @returns {string|null} formatted timestamp in the user's timezone, or null
 */
export const formatLatestUpdate = value => {
  if (!value) return null

  const storedTimezone = loadStateFromLocalStorage('timezone')
  const timezone = storedTimezone !== undefined ? storedTimezone : 'Etc/UTC'

  // Parse as UTC (backend sends no zone suffix), then convert to the user's zone
  return momentTimezone.utc(value).tz(timezone).format('YYYY-MM-DD HH:mm')
}

/**
 * Transform the raw portfolio payload (capacities in kW, flat summary fields)
 * into the shape the UI expects: a nested `summary`, per-country `mw` values,
 * and a formatted `latestUpdate`. Reused for both the mock file and the future
 * axios GET response.
 * @param {Object} raw - raw API/mock payload
 * @returns {{summary: Object, latestUpdate: string|null, countries: Array}|null}
 */
export const transformPortfolioData = raw => {
  if (!raw) return null

  return {
    summary: {
      numberOfCountries: raw.numberOfCountries,
      totalCapacity: kwToMw(raw.totalCapacity),
      avgMonthlyProduction: raw.avgMonthlyProduction,
      activeProjects: raw.activeProjects,
      totalSetups: raw.totalSetups,
    },
    latestUpdate: formatLatestUpdate(raw.latest_update),
    countries: (raw.countries || []).map(country => ({
      ...country,
      solar: country.solar
        ? { ...country.solar, mw: kwToMw(country.solar.kw) }
        : country.solar,
      wind: country.wind
        ? { ...country.wind, mw: kwToMw(country.wind.kw) }
        : country.wind,
    })),
  }
}

export default transformPortfolioData
